import { invalidateAirlines } from '@/lib/api/airlines';
import { api } from '@/lib/api/client';
import type { ServerResponse } from '@/lib/api/types';
import type { Airline } from '@/types/airline';

/**
 * The airline dictionary.
 *
 * Reading is open to `ADMIN`, `AAK`, `USER` and `KGA_USER`; **writing is ADMIN
 * only**, and a refusal there is a transport-level `403` rather than a
 * `successfull: false` body. The two are different answers and the screen keeps them
 * apart: one means "not permitted", the other means "that was not a valid airline".
 */

/**
 * The whole dictionary — about 1035 carriers, sorted by title.
 *
 * **`/airline/list`, not `/airline/all`.** The endpoint named "all" predates this one
 * and returns only the carriers on the working list, so backing an administration
 * table with it would silently hide every row an administrator most needs to find.
 * There is no paged variant: the table pages this array itself.
 */
export const fetchAirlineDictionary = (signal?: AbortSignal): Promise<Airline[]> =>
  api
    .get<Airline[]>('/airline/list', undefined, signal)
    .then((response) => (Array.isArray(response) ? response : []));

/**
 * One airline.
 *
 * **A missing id is `200 null`, not a 404**, so "the request worked" does not mean
 * "the airline exists".
 */
export const fetchAirline = (id: number, signal?: AbortSignal): Promise<Airline | null> =>
  api.get<Airline | null>(`/airline/id/${id}`, undefined, signal);

/**
 * The outcome of a write, as a union so the check cannot be skipped.
 *
 * A rejected write — a duplicate code, a code of the wrong length, an id that does
 * not exist — arrives as **HTTP 200** with `successfull: false` (the backend's own
 * spelling). Branching on the status code reports every one of those as a success.
 */
export type AirlineWriteResult =
  | { ok: true; airline: Airline }
  | { ok: false; message: string | null };

/** Delete answers with `payload: null` even on success, so it has no airline to carry. */
export type AirlineDeleteResult = { ok: true } | { ok: false; message: string | null };

const settle = (response: ServerResponse<Airline>): AirlineWriteResult => {
  if (response.successfull && response.payload !== null) {
    // The dictionary just moved, and the board's carrier list is a cached copy of
    // part of it in this same tab.
    invalidateAirlines();
    return { ok: true, airline: response.payload };
  }

  return { ok: false, message: response.errorMessage };
};

/**
 * Creates an airline.
 *
 * `id` in the body is ignored; the three codes are trimmed and upper-cased, and the
 * IATA code must not already exist.
 *
 * Most real designators are already in the seeded dictionary, so a create usually
 * belongs to a newly launched carrier — correcting an existing row is an edit.
 */
export const createAirline = (airline: Partial<Airline>): Promise<AirlineWriteResult> =>
  api.post<ServerResponse<Airline>>('/airline/add', airline).then(settle);

/**
 * Updates an airline.
 *
 * **This is a REPLACE.** Every field left out of the body is written as `null` or
 * `false` — the API says so outright — so callers build the body by spreading the row
 * they loaded. See `AirlinePanel`.
 *
 * The path `{id}` is authoritative, so an `id` in the body is ignored.
 */
export const editAirline = (id: number, airline: Partial<Airline>): Promise<AirlineWriteResult> =>
  api.post<ServerResponse<Airline>>(`/airline/edit/id/${id}`, airline).then(settle);

/**
 * Removes an airline. **The row is gone** — there is no soft delete and no active
 * flag on this table.
 *
 * Schedules keep the carrier's IATA code as plain text with no foreign key, so
 * deleting a carrier that still appears in them leaves those flights with a code that
 * resolves to nothing. Dropping a carrier from the working list is `editAirline` with
 * `kzFlying: false`, and that is what the screen offers first.
 */
export const deleteAirline = (id: number): Promise<AirlineDeleteResult> =>
  api.post<ServerResponse<null>>(`/airline/delete/id/${id}`).then((response) => {
    if (response.successfull) {
      invalidateAirlines();
      return { ok: true };
    }

    return { ok: false, message: response.errorMessage };
  });
